import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/pages/home/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { DEMO_CREDENTIALS, type DemoKind } from "@/lib/demo";

interface SignInModalProps {
  open: boolean;
  onClose: () => void;
}

type View = "signin" | "forgot" | "forgot_sent";

export default function SignInModal({ open, onClose }: SignInModalProps) {
  const { isAr, t } = useLang();
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleClose = () => {
    setView("signin");
    setError("");
    onClose();
  };

  if (!open) return null;

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError(t("Please fill in all fields.", "يرجى ملء جميع الحقول."));
      return;
    }
    setLoading(true);
    const { error: err } = await signIn(email.trim(), password);
    setLoading(false);
    if (err) {
      setError(t("Invalid email or password. Please try again.", "البريد الإلكتروني أو كلمة المرور غير صحيحة. حاول مرة أخرى."));
      return;
    }
    handleClose();
    navigate("/dashboard");
  };

  /** Auto sign-in with a demo account. The two seeded accounts live in
   *  Supabase with pre-confirmed emails (migration 006) — click a
   *  button, hit the API, land on the dashboard. Used on investor /
   *  customer demos where we want the evaluator in the app in one
   *  click. Remove or gate behind an env flag before onboarding real
   *  paying users (sprint 5). */
  const handleDemoSignIn = async (kind: DemoKind) => {
    setError("");
    setLoading(true);
    const creds = DEMO_CREDENTIALS[kind];
    const { error: err } = await signIn(creds.email, creds.password);
    setLoading(false);
    if (err) {
      setError(t("Demo sign-in failed.", "فشل الدخول التجريبي."));
      return;
    }
    handleClose();
    navigate("/dashboard");
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!resetEmail.trim()) {
      setError(t("Please enter your email address.", "يرجى إدخال بريدك الإلكتروني."));
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setView("forgot_sent");
  };

  const inputClass = `w-full px-4 py-3 rounded-xl text-sm border border-line-strong bg-white outline-none focus:border-gold transition-colors text-ink placeholder-[#B0A898] ${isAr ? "font-tajawal text-right" : ""}`;
  const labelClass = `block text-sm font-medium text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-gold via-[#E8C060] to-gold-dark" />

        <div className="p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-ink flex items-center justify-center flex-shrink-0">
                  <i className="ri-door-open-line text-white text-sm"></i>
                </div>
                <span className={`font-bold text-base text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
                  ضيافة Diyafa
                </span>
              </div>
              {view === "signin" && (
                <>
                  <h2 className={`font-bold text-2xl text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
                    {t("Welcome back", "مرحباً بعودتك")}
                  </h2>
                  <p className={`text-sm text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
                    {t("Sign in to your Diyafa dashboard", "سجّل دخولك إلى لوحة تحكم ضيافة")}
                  </p>
                </>
              )}
              {(view === "forgot" || view === "forgot_sent") && (
                <>
                  <h2 className={`font-bold text-2xl text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
                    {t("Reset Password", "إعادة تعيين كلمة المرور")}
                  </h2>
                  <p className={`text-sm text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
                    {t("We'll send you a reset link", "سنرسل لك رابط إعادة التعيين")}
                  </p>
                </>
              )}
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-muted hover:bg-[#F5EDD8] hover:text-ink transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* ── SIGN IN VIEW ── */}
          {view === "signin" && (
            <>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label className={labelClass}>{t("Email Address", "البريد الإلكتروني")}</label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ahmed@hotel.om"
                      className={inputClass}
                      autoComplete="email"
                    />
                    <div className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none ${isAr ? "left-3" : "right-3"}`}>
                      <i className="ri-mail-line text-[#B0A898] text-sm"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={`text-sm font-medium text-ink-soft ${isAr ? "font-tajawal" : ""}`}>
                      {t("Password", "كلمة المرور")}
                    </label>
                    <button
                      type="button"
                      onClick={() => { setResetEmail(email); setView("forgot"); setError(""); }}
                      className={`text-xs text-gold hover:text-gold-dark cursor-pointer font-medium ${isAr ? "font-tajawal" : ""}`}
                    >
                      {t("Forgot password?", "نسيت كلمة المرور؟")}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("Enter your password", "أدخل كلمة المرور")}
                      className={`${inputClass} ${isAr ? "pl-10" : "pr-10"}`}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#B0A898] hover:text-ink-soft cursor-pointer transition-colors ${isAr ? "left-3" : "right-3"}`}
                    >
                      <i className={`text-sm ${showPass ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100">
                    <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
                    <p className={`text-xs text-red-500 ${isAr ? "font-tajawal" : ""}`}>{error}</p>
                  </div>
                )}

                <div className="flex items-center gap-2.5">
                  <input type="checkbox" id="remember" className="w-4 h-4 rounded border-line-strong accent-gold cursor-pointer" />
                  <label htmlFor="remember" className={`text-sm text-muted cursor-pointer ${isAr ? "font-tajawal" : ""}`}>
                    {t("Remember me for 30 days", "تذكّرني لمدة 30 يوماً")}
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3.5 rounded-xl font-semibold text-ink bg-gold hover:bg-gold-dark cursor-pointer transition-all disabled:opacity-70 whitespace-nowrap mt-2 ${isAr ? "font-tajawal" : ""}`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      {t("Signing in...", "جارٍ تسجيل الدخول...")}
                    </span>
                  ) : t("Sign In", "تسجيل الدخول")}
                </button>
              </form>

              {/* ── Demo account quick-login ───────────────────────
               *  Two one-click buttons for evaluators: a hotel-only
               *  tenant and a rental-only tenant. Shows, at a glance,
               *  that Diyafa splits the UX by property type.
               *  The accounts are seeded in the DB with pre-confirmed
               *  emails and fixed passwords — they're for demo only.  */}
              <div className="mt-6">
                <div className={`flex items-center gap-3 mb-3 ${isAr ? "flex-row-reverse" : ""}`}>
                  <div className="flex-1 h-px bg-line-strong" />
                  <span className={`text-[10px] text-muted uppercase tracking-wider ${isAr ? "font-tajawal" : ""}`}>
                    {t("or try a demo", "أو جرّب الوضع التجريبي")}
                  </span>
                  <div className="flex-1 h-px bg-line-strong" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleDemoSignIn("hotel")}
                    disabled={loading}
                    className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl border border-line-strong bg-white hover:bg-cream cursor-pointer transition-colors disabled:opacity-60 ${isAr ? "font-tajawal" : ""}`}
                  >
                    <i className="ri-building-line text-gold text-lg"></i>
                    <span className="text-xs font-semibold text-ink">
                      {t("Hotel demo", "تجربة فندق")}
                    </span>
                    <span className="text-[10px] text-muted">
                      {t("Al Muraqqa · 15 rooms", "المرقاع · 15 غرفة")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDemoSignIn("rental")}
                    disabled={loading}
                    className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl border border-line-strong bg-white hover:bg-cream cursor-pointer transition-colors disabled:opacity-60 ${isAr ? "font-tajawal" : ""}`}
                  >
                    <i className="ri-car-line text-gold text-lg"></i>
                    <span className="text-xs font-semibold text-ink">
                      {t("Rental demo", "تجربة تأجير")}
                    </span>
                    <span className="text-[10px] text-muted">
                      {t("Nizwa Fleet · 8 cars", "أسطول نزوى · 8 سيارات")}
                    </span>
                  </button>
                </div>
              </div>

              <p className={`text-center text-xs text-muted mt-6 ${isAr ? "font-tajawal" : ""}`}>
                {t("Don't have an account?", "ليس لديك حساب؟")}{" "}
                <a href="/register" className="text-gold font-semibold hover:text-gold-dark cursor-pointer">
                  {t("Start Free Trial", "ابدأ تجربتك المجانية")}
                </a>
              </p>
            </>
          )}

          {/* ── FORGOT PASSWORD VIEW ── */}
          {view === "forgot" && (
            <>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className={labelClass}>{t("Email Address", "البريد الإلكتروني")}</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="ahmed@hotel.om"
                      className={inputClass}
                      autoComplete="email"
                    />
                    <div className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none ${isAr ? "left-3" : "right-3"}`}>
                      <i className="ri-mail-line text-[#B0A898] text-sm"></i>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100">
                    <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
                    <p className={`text-xs text-red-500 ${isAr ? "font-tajawal" : ""}`}>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3.5 rounded-xl font-semibold text-ink bg-gold hover:bg-gold-dark cursor-pointer transition-all disabled:opacity-70 whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      {t("Sending...", "جارٍ الإرسال...")}
                    </span>
                  ) : t("Send Reset Link", "إرسال رابط الإعادة")}
                </button>
              </form>

              <button
                onClick={() => { setView("signin"); setError(""); }}
                className={`mt-4 w-full text-center text-xs text-muted hover:text-gold cursor-pointer transition-colors ${isAr ? "font-tajawal" : ""}`}
              >
                ← {t("Back to Sign In", "العودة لتسجيل الدخول")}
              </button>
            </>
          )}

          {/* ── FORGOT SENT VIEW ── */}
          {view === "forgot_sent" && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-send-line text-green-500 text-2xl"></i>
              </div>
              <h3 className={`font-bold text-lg text-ink mb-2 ${isAr ? "font-tajawal" : "font-inter"}`}>
                {t("Check your inbox!", "تحقق من بريدك!")}
              </h3>
              <p className={`text-sm text-muted mb-6 leading-relaxed ${isAr ? "font-tajawal" : ""}`}>
                {t(
                  `We sent a password reset link to ${resetEmail}. Check your spam folder if you don't see it.`,
                  `أرسلنا رابط إعادة تعيين كلمة المرور إلى ${resetEmail}. تحقق من مجلد الرسائل غير المرغوب فيها إذا لم تجده.`
                )}
              </p>
              <button
                onClick={() => { setView("signin"); setError(""); }}
                className={`px-6 py-2.5 rounded-xl bg-gold text-ink text-sm font-semibold hover:bg-gold-dark cursor-pointer transition-colors whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}
              >
                {t("Back to Sign In", "العودة لتسجيل الدخول")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
